/* eslint-disable @next/next/no-img-element */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'

import axios from 'axios'
import Backend from '../../utils/Backend'
import useMobile from '../../components/Hooks/Mobile'
import renderVehicleTitle from '../../helper/vehicle_title_render'
import fetchColors from '../../helper/fetch_colors'
import generateURL from '../../helper/vehicle_url'
import { googleAdIds, imageAngles } from '../../helper/constants'
import { toast } from 'react-toastify'

import { isFinance } from '../../helper/is_finance'

import { event } from '../../lib/gtag';

import IOption from '../../interfaces/IOption'
import IImage from '../../interfaces/IImage'

import Footer from '../Footer'
import Header from '../Header'
import Loader from '../Loader'
import PageHeader from '../PageHeader'
import FinanceHeader from '../FinanceHeader'
import OptionCard from '../Cards/Vehicle/OptionCard'
import ColourBubbles from '../ColourBubbles'
import OptionsSelector from '../OptionsSelector'
import YoutubeEmbed from '../YouTubeEmbed'
import VehicleNotification from '../VehicleNotification'
import GoogleAd from '../GoogleAd'

import PoweredByText from '../PoweredBy'
import RegulatoryStatements from '../RegulatoryStatements'

import styles from './styles.module.scss'
import HeaderBackground from '../../public/assets/images/options-background.jpg'
import ShareIcon from '../../public/assets/icons/share'
import BrochureIcon from '../../public/assets/icons/brochure'
import PlayIcon from '../../public/assets/icons/play'
import CompareIcon from '../../public/assets/icons/compare'
import ShareVehicle from '../../public/assets/icons/share-vehicle'
import ArrowIcon from '../../public/assets/icons/right-arrow.svg'
import ArrowIconBoi from '../../public/assets/nevo-boi/chevron-right.svg'
import { useIsBoiWebsite } from "../Context/WebsiteType"
import { isBOIMake } from "../../helper/functions/isBoiMake"

interface IVehicleOptions {
  baseVehicle: any;
  commercial?: boolean;
  financePage?: boolean;
}

export default function VehicleOptions({ baseVehicle, commercial = false, financePage = false }: IVehicleOptions) {
  const [image, setImage] = useState(0)
  const [loading, setLoading] = useState(true)
  const [carouselImages, setCarouselImages] = useState<IImage[]>([]) 
  
  const [preloadImages, setPreloadImages] = useState(true)
  const [loadedAllImages, setLoadedAllImages] = useState(false)

  const [color, setColor] = useState<IOption | null>()
  const [colors, setColors] = useState<IOption[]>([])
  
  const [animationImage, setAnimationImage] = useState<number>(0)
  const [animation, setAnimation] = useState(false)
  const imageRef = useRef<HTMLImageElement>(null)
  const animationImageRef = useRef<HTMLImageElement>(null)

  // for showing list of options
  const [options, setOptions] = useState<any[]>([]);
  const [allOptions, setAllOptions] = useState<any[]>([]);
  const [optionsError, setOptionsError] = useState("")
  
  // for actions
  const [video, setVideo] = useState("");
  const [build, setBuild] = useState("");
  const [brochure, setBrochure] = useState("");

  // for selecting bodyType
  const [bodyType, setBodyType] = useState<string>("")
  const [bodyOptions, setBodyOptions] = useState<string[]>([])

  // for review 
  const [playVideo, setPlayVideo] = useState(false);

  const [notification, setNotification] = useState(false) 

  const router = useRouter();
  const mobile = useMobile(768);

  const isBoiWebsite = useIsBoiWebsite();

  // notification 
  useEffect(() => {
      setNotification(true)
  }, [])

  // process colors for the color picker
  useEffect(() => {
    if (baseVehicle) {
      const success = (colors: IOption[]) => {
        setColor(colors[0])
        setColors(colors)
      }
      const error = () => {
        setColor({ label: "", value: "imagin-grey" })
      }

      fetchColors(baseVehicle, success, error)
    }
  }, [baseVehicle])

  // fetching and processing images for Carousel
  useEffect(() => {
    if (color && baseVehicle) {
      setLoading(true)

      const colorCode = color.value.toString().split("&")[0]

      const imagesURL: IImage[] = []
      imageAngles.forEach(angle => {
        const url = Backend.getImage(baseVehicle, angle, colorCode)        
        imagesURL.push({
          image: url,
          preloaded: false
        })
      });

      setCarouselImages(imagesURL)

      setTimeout(() => { // giving time to load images from imagin-studio
        setLoading(false);
      }, 500)
    }
  }, [baseVehicle, color])

  const preloadImage = (image: IImage) => {
    if (!image.preloaded) {
      image.preloaded = true
      
      // preloading image part
      const img = new Image()
      img.src = image.image;
    }
  }

  // to start preloading images
  useEffect(() => {
    setPreloadImages(true)
  }, [image, color])
  // when the color has been changed, we need to preload images again with new color
  useEffect(() => {
    setLoadedAllImages(false)
  }, [color])

  // preloading 5 images instead of all 8 (most of times saves 3 images load) 
  useEffect(() => {
    if (carouselImages.length > 0 && preloadImages && !loadedAllImages) {
      setPreloadImages(false);

      const images = [...carouselImages]
      
      const prev = image > 0 ? image - 1 : carouselImages.length - 1;
      const prevPrev = prev > 0 ? prev - 1 : carouselImages.length - 1;

      const next = image < carouselImages.length - 1 ? image + 1 : 0;
      const nextNext = next < carouselImages.length - 1 ? next + 1 : 0;
      
      preloadImage(images[image])
      preloadImage(images[prev])
      preloadImage(images[prevPrev])
      preloadImage(images[next])
      preloadImage(images[nextNext])

      // if loaded all images
      if (images.findIndex(img => !img.preloaded) === -1) {
        setLoadedAllImages(true)
      }

      setCarouselImages(images)
    }
  }, [carouselImages, image, loadedAllImages, preloadImages])


  const proccessBodyTypes = (options: any[]) => {
    const allBodyTypes : string[] = []

    options.forEach(option => {
      const index = allBodyTypes.findIndex(type => {
        return type.toLowerCase() === option.body_type.toLowerCase();
      });

      if (index === -1) { // not found
        allBodyTypes.push(option.body_type)
      }
    })

    if (allBodyTypes.length > 1) {
      setBodyOptions(allBodyTypes);
      setBodyType(allBodyTypes[0]);
    }
  }

  const processAction = (options: any[], action: string, callback: (value: string) => void) => {
    for (let option of options) {
      if (option[action]?.length > 0) {
        callback(option[action])
        break;
      }
    } 
  }

  // fetch options
  useEffect(() => {
    if (baseVehicle) {
      const { promise, cancel } = Backend.getOptions(
        baseVehicle.global_data.manufacturer, baseVehicle.third_party.modelId, 
        (!financePage) ? commercial : undefined,
          financePage
      )
      
      promise.then(({ data }) => {
        if (data.status === 200 && data.content?.vehicles?.length > 0) {
          const fetchedVehicles = data.content.vehicles;
          
          setOptions(fetchedVehicles);
          setAllOptions(fetchedVehicles);
          
          proccessBodyTypes(fetchedVehicles);
          
          // process actions
          processAction(fetchedVehicles, "video_url", setVideo)
          processAction(fetchedVehicles, "build_url", setBuild)
          processAction(fetchedVehicles, "brochure_url", setBrochure)
        }
        else {
          setOptionsError("No options were found")
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;

        setOptionsError(`Failed to fetch options for ${baseVehicle.make} ${baseVehicle.model}`)
      })

      return cancel;
    }
  }, [baseVehicle, commercial, financePage])

  // filter options based on selected body types
  useEffect(() => {
    if (bodyOptions.length < 2) return;

    if (bodyType !== "") {
      const filteredOptions = allOptions.filter(option => {
        return bodyType === option.body_type
      })

      setOptions(filteredOptions);
    }
  }, [bodyType, bodyOptions, allOptions]);

  // direction: true scroll to next, false scroll to prev
  const animate = (newImage: number, direction: boolean) => {
    setAnimation(true)

    const imageClass = direction ? styles.imageDisappearNext: styles.imageDisappearPrev
    const animationImageClass = direction ? styles.imageAppearNext: styles.imageAppearPrev

    // animation for main image
    imageRef?.current?.classList.add(imageClass)

    setTimeout(() => {
      setImage(newImage)
      imageRef?.current?.classList.remove(imageClass)
    }, 500)

    // animation for animation image
    setTimeout(() => {
      animationImageRef?.current?.classList.add(animationImageClass)
    }, 1)
    setTimeout(() => {
      animationImageRef?.current?.classList.remove(animationImageClass) 
      setAnimation(false)
    }, 500)
  }
    
  const prevImage = () => {
    if (animation) return

    let prevImage = image - 1
    if (prevImage < 0) {
      prevImage = carouselImages.length - 1
    }
    setAnimationImage(prevImage)
    animate(prevImage, false)
  }
  const nextImage = () => {
    if (animation) return

    let nextImage = image + 1
    if (nextImage >= carouselImages.length) {
      nextImage = 0
    }
    setAnimationImage(nextImage)
    animate(nextImage, true)
  }

  const openBaseOption = (trackEvent = true) => {
    if (baseVehicle && router.query) {
      if (trackEvent) event("testDriveEnquire")
      
      const { make, model } = router.query;

      const base_url = "vehicle";
      router.push(`/${base_url}/option/${make}/${model}/${baseVehicle.third_party.variantId}`)
    }
  }

  const financeVehicle = useCallback(() => {
    if (baseVehicle && router.query) {
      event(financePage ? "financeVehicleOnFinanceClick" : "financeVehicleClick")

      const { make, model } = router.query;
      router.push(`/finance-vehicle/option/${make}/${model}/${baseVehicle.third_party.variantId}`)
    }
  }, [baseVehicle, financePage, router])

  const shareVehicle = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      toast.success("Share link copied to clipboard");
    });
  }
  
    /*  For non BOI brands in the full vehicle ‘on the road’ flow, a new ‘Finance Vehicle” button will be added.
        This will be a simple link to the BOI Car Loans site https://personalbanking.bankofireland.com/borrow/loans/car-loan/ 
        This will open in a new browser tab
    */

  const financeVehicleWithBOI = useCallback(() => {
    event('financeVehicleForBOIClick')
    window.open('https://personalbanking.bankofireland.com/borrow/loans/car-loan/', '_blank')
  }, [])


  // center action buttons on mobile if there are only 2
  const centerActions = useMemo(() => {
    return (!!!build && !!!brochure && !!!video) && styles.centerActions
  }, [build, video, brochure])

  const isEligableFinance = useMemo(() => {
    return isFinance(baseVehicle) && isBOIMake(baseVehicle.make)
  }, [baseVehicle])

  const renderedFinanceButton = useMemo(() => {
    if (isEligableFinance && isBOIMake(baseVehicle.make)) {
      return (
        <section className={isBoiWebsite ? styles.leaseActionBoi : styles.leaseAction}>
          <button onClick={financeVehicle} className={`${isBoiWebsite ? styles.blueButtonBoi : styles.blueButton} ${isBoiWebsite ? 'dark-blue-button-boi' : 'blue-button'}`}>
            Finance Vehicle
          </button>
  
          <PoweredByText 
            text='Bank of Ireland' 
            textClassName={isBoiWebsite ? styles.bankOfIrelandTextBoi : styles.bankOfIrelandText}
          />
        </section>
      )
    }
  }, [financeVehicle, isEligableFinance, isBoiWebsite, baseVehicle])

  const renderedFinanceVehicleWithBOIButton = useMemo(() => {
    if (!isEligableFinance) {
      return (
        <section className={isBoiWebsite ? styles.leaseActionBoi : styles.leaseAction}>
          <button onClick={financeVehicleWithBOI} className={`${isBoiWebsite ? styles.blueButtonBoi : styles.blueButton} ${isBoiWebsite ? 'dark-blue-button-boi' : 'blue-button'}`}>
            Finance Vehicle
          </button>
  
        </section>
      )
    }
  }, [financeVehicleWithBOI, isEligableFinance, isBoiWebsite])

  const description : string = useMemo(() => {
    if (!!baseVehicle.description) return baseVehicle.description;

    for (const option of allOptions) {
      if (!!option.description) {
        return option.description;
      }
    }

    return "";
  }, [baseVehicle, allOptions])

  const googleAdModel = useMemo(() => {
    if (router.isReady) {
      const model = router.query.model as string;
      return model;
    }
  }, [router])

  return (
    <>
      <Head>
        <title>{financePage ? 'Finance ' : ''}{renderVehicleTitle(baseVehicle)} | Nevo</title>
      </Head>

      <Header transparent blackStyle={!financePage} black={financePage} hideBanner/>

      { financePage && 
        <FinanceHeader />
      }

      <PageHeader 
        staticHeader backgroundImage={HeaderBackground.src} 
        className={`${isBoiWebsite ? styles.vehiclePageHeaderBoi : styles.vehiclePageHeader} ${financePage && styles.leasePageHeader} ${financePage && styles.isFinancePage}`}
      >
        { baseVehicle ? 
          <article className={`${isBoiWebsite ? styles.vehiclePageHeaderContentBoi : styles.vehiclePageHeaderContent} ${financePage && styles.isFinancePage}`}>

            <div className={`${isBoiWebsite ? styles.carouselBoi : styles.carousel} ${financePage && styles.isFinancePage}`}>
              
              <div onClick={prevImage} className={`${isBoiWebsite ? styles.leftArrowBoi : styles.leftArrow} ${isBoiWebsite ? styles.arrowHolderBoi : styles.arrowHolder}`}>
                <img src={isBoiWebsite ? ArrowIconBoi.src : ArrowIcon.src} alt='left arrow'/>
              </div>

              { (!loading && carouselImages.length > 0) ? (
                <div className={isBoiWebsite ? styles.carouselImageBoi : styles.carouselImage}>
                  { animation && (
                    <img ref={animationImageRef} src={carouselImages[animationImage].image} className={isBoiWebsite ? styles.animationImageBoi : styles.animationImage} alt='vehicle image' />
                  )}
                  <img ref={imageRef} src={carouselImages[image].image} alt='vehicle image' />
                </div>
              ) : <Loader /> }

              <div onClick={nextImage} className={`${isBoiWebsite ? styles.rightArrowBoi : styles.rightArrow} ${isBoiWebsite ? styles.arrowHolderBoi : styles.arrowHolder}`}>
                <img src={isBoiWebsite ? ArrowIconBoi.src : ArrowIcon.src} alt='right arrow'/>
              </div>

            </div>

            <div className={isBoiWebsite ? styles.vehicleDetailsBoi : styles.vehicleDetails}>
              { colors.length > 0 && (
                <div className={isBoiWebsite ? styles.colorsSelectorBoi : styles.colorsSelector}>
                  <p className={isBoiWebsite ? styles.chooseColorBoi : styles.chooseColor}>Choose your colour</p>
                  
                  <ColourBubbles color={color} setColor={setColor} colors={colors} />

                  <p className={isBoiWebsite ? styles.selectedColorBoi : styles.selectedColor}>{color?.value.toString().split("&")[1]}</p>
                </div>
              )}

              <h1>{`${baseVehicle.make} ${baseVehicle.model}`}</h1>

              <article className={isBoiWebsite ? styles.headerActionsBoi : styles.headerActions}>
                { (!financePage) && (
                  <button onClick={() => openBaseOption()} className={` ${isBoiWebsite ? styles.blueButtonBoi : styles.blueButton} ${isBoiWebsite ? 'dark-blue-button-boi' : 'blue-button'}`}>
                    Test Drive <span>/</span> Enquire
                  </button>
                )}

                { isEligableFinance ? renderedFinanceButton : isBoiWebsite ? renderedFinanceVehicleWithBOIButton : null }

              </article>
              
            </div>

          </article>
          : <Loader />
        }
      </PageHeader>

      {/* render fetch options or error message */}
      { baseVehicle && (
        <>
          <div className={`${isBoiWebsite ? styles.optionsPageBoi : styles.optionsPage} page-holder`}>
            
            <div className={`${isBoiWebsite ? styles.actionsHolderBoi : styles.actionsHolder} ${centerActions}`}>                    
              { !!build &&
                <a href={build} target='_blank' rel='noreferrer'>
                  <div 
                    className={`${isBoiWebsite ? 'light-blue-button-boi action-button-boi' : 'blue-button action-button'}`}
                    onClick={() => event('actionBuildClick')}
                  >
                    <span>Build</span>
                    <ShareIcon className={isBoiWebsite ? styles.shareIconBoi : styles.shareIcon} />
                  </div>
                </a>
              }

              { !!brochure &&
                <a href={brochure} target='_blank' rel='noreferrer'>
                  <div 
                    className={`${isBoiWebsite ? 'light-blue-button-boi action-button-boi' : 'blue-button action-button'}`}
                    onClick={() => event('actionBrochureClick')}
                  >
                    <span>Brochure</span>
                    <BrochureIcon color={isBoiWebsite ? '#0000FF' : '#FFFFFF'}/>
                  </div>
                </a>
              }
              
              { !!video &&
                <div 
                  className={`${isBoiWebsite ? 'light-blue-button-boi action-button-boi' : 'blue-button action-button'}`}
                  onClick={() => {
                    event('actionReviewClick')
                    setPlayVideo(true)
                  }}
                >
                  <span>Review</span>
                  <PlayIcon color={isBoiWebsite ? '#0000FF' : '#FFFFFF'}/>
                </div>
              }

              <a href={`/vehicles-comparison?${generateURL(baseVehicle, 1)}`} target='_blank' rel='noreferrer'>
                <div 
                  className={`${isBoiWebsite ? 'light-blue-button-boi action-button-boi' : 'blue-button action-button'}`}
                  onClick={() => event('actionCompareClick')}
                >
                  <span>Compare</span>
                  <CompareIcon  color={isBoiWebsite ? '#0000FF' : '#FFFFFF'}/>
                </div>           
              </a>

              <div 
                className={`${isBoiWebsite ? 'light-blue-button-boi action-button-boi' : 'blue-button action-button'}`}
                onClick={() => {
                  event('actionShareClick')
                  shareVehicle()
                }}
              >
                <span>Share</span>
                <ShareVehicle color={isBoiWebsite ? '#0000FF' : '#FFFFFF'}/>
              </div>
            </div>

            { optionsError !== "" 
              ? <p className={isBoiWebsite ? styles.errorMessageBoi : styles.errorMessage}>{ optionsError }</p>
              : (allOptions.length === 0 
                ? <Loader className={isBoiWebsite ? styles.loaderBoi : styles.loader} />
                : 
                <div className={isBoiWebsite ? styles.optionsBoi : styles.options}>

                  <div className={isBoiWebsite ? styles.descriptionHolderBoi : styles.descriptionHolder}>
                    <p className={isBoiWebsite ? styles.descriptionTextBoi : styles.descriptionText}>
                      { description }
                    </p>
                  </div>

                  {(!isBoiWebsite && googleAdModel) && 
                    <GoogleAd 
                      adId={googleAdIds.horizontal_banner}
                      className={isBoiWebsite ? styles.adBannerBoi : styles.adBanner}
                      model={googleAdModel}
                      commercial={commercial}
                    />
                  }

                  <p className={isBoiWebsite ? styles.chooseTextBoi : styles.chooseText}>
                    Choose the {baseVehicle.model}{(mobile && <br/>)} that&apos;s right for you:
                  </p>

                  { bodyOptions.length > 1 && (
                    <div className={isBoiWebsite ? styles.bodySelectorHolderBoi : styles.bodySelectorHolder}>
                      <OptionsSelector 
                        allOptions={bodyOptions}
                        selectedOption={bodyType}
                        setSelectedOption={setBodyType}
                      />
                    </div>
                  )}

                  <div className={isBoiWebsite ? styles.optionsListBoi : styles.optionsList}>
                    { 
                      options.map((option) => {
                        return (
                          <article key={option.third_party.versionId}>
                            <OptionCard 
                              option={option}
                              financePage={financePage}
                              commercial={commercial}
                              isEligableFinance={isEligableFinance}
                            />
                          </article>
                        )
                      })
                    }
                  </div>
                </div>
              )
            }
          </div>
        </>
      )}

      { !!video && 
        <YoutubeEmbed videoURL={video} playVideo={playVideo} setPlayVideo={setPlayVideo} />
      }


      {(notification && baseVehicle && !financePage) &&
        <VehicleNotification openBaseOption={openBaseOption} close={() => setNotification(false)} />
      }
      
      <Footer />
    </>
  );
};
